import BaseModel from './Base.model';

/**
 * Model đại diện cho một sản phẩm
 */
export default class ProductModel extends BaseModel {
  /**
   * Tạo một instance mới của Product
   * @param {Object} data - Dữ liệu sản phẩm
   */
  /**
   * Represents a Product model.
   *
   * @param {Object} [data={}] - The data to initialize the Product model.
   * @param {string} [data.id] - The unique identifier of the product.
   * @param {string} [data.createdAt] - The creation timestamp of the product.
   * @param {string} [data.updatedAt] - The last update timestamp of the product.
   * @param {string} [data.code=''] - The product code.
   * @param {string} [data.name=''] - The name of the product.
   * @param {string} [data.categoryId=''] - The ID of the product's category.
   * @param {string} [data.uomId=''] - The ID of the unit of measure.
   * @param {string} [data.vatType=''] - The VAT type of the product.
   * @param {string} [data.colorExterior=''] - The exterior color of the product.
   * @param {string} [data.colorInterior=''] - The interior color of the product.
   * @param {string|null} [data.productSegmentId1=null] - The first product segment ID.
   * @param {string|null} [data.productSegmentId2=null] - The second product segment ID.
   * @param {string|null} [data.productSegmentId3=null] - The third product segment ID.
   * @param {string|null} [data.productSegmentId4=null] - The fourth product segment ID.
   * @param {string|null} [data.createdBy=null] - The ID of the user who created the product.
   * @param {string|null} [data.updatedBy=null] - The ID of the user who last updated the product.
   * @param {string|null} [data.description=null] - The description of the product.
   * @param {number} [data.deleted=0] - Indicates if the product is deleted (0 for false, 1 for true).
   * @param {string|null} [data.parentId=null] - The ID of the parent product, if any.
   * @param {number} [data.importPrice=0] - The import price of the product.
   * @param {number} [data.sellingPrice=0] - The selling price of the product.
   * @param {string} [data.brandId=''] - The ID of the brand associated with the product.
   * @param {string|null} [data.companyId=null] - The ID of the company associated with the product.
   * @param {string} [data.version=''] - The version of the product.
   * @param {string} [data.type=''] - The type of the product (e.g., 'INDEPENDENT').
   */
  constructor(data = {}) {
    super({
      id: data?.id,
      createdAt: data?.createdAt,
      updatedAt: data?.updatedAt,
    });
    this.code = data?.code || '';
    this.name = data?.name || '';
    this.categoryId = data?.categoryId || '';
    this.uomId = data?.uomId || '';
    this.vatType = data?.vatType || '';
    this.colorExterior = data?.colorExterior || '';
    this.colorInterior = data?.colorInterior || '';
    this.productSegmentId1 = data?.productSegmentId1 || null;
    this.productSegmentId2 = data?.productSegmentId2 || null;
    this.productSegmentId3 = data?.productSegmentId3 || null;
    this.productSegmentId4 = data?.productSegmentId4 || null;
    this.createdBy = data?.createdBy || null;
    this.updatedBy = data?.updatedBy || null;
    this.description = data?.description || null;
    this.deleted = data?.deleted !== undefined ? data?.deleted : 0;
    this.parentId = data?.parentId || null;
    this.importPrice = data?.importPrice || 0;
    this.sellingPrice = data?.sellingPrice || 0;
    this.brandId = data?.brandId || '';
    this.companyId = data?.companyId || null;
    this.version = data?.version || '';
    this.type = data?.type || '';
  }

  /**
   * Chuyển đổi từ dữ liệu JSON thành đối tượng Product
   * @param {Object} json - Dữ liệu JSON
   * @returns {ProductModel} - Đối tượng Product
   */
  static fromJson(json) {
    return BaseModel.fromJson(json, ProductModel);
  }

  /**
   * Chuyển đổi từ danh sách JSON thành danh sách đối tượng Product
   * @param {Array} jsonArray - Mảng dữ liệu JSON
   * @returns {Array<ProductModel>} - Mảng đối tượng Product
   */
  static fromJsonArray(jsonArray) {
    return BaseModel.fromJsonArray(jsonArray, ProductModel);
  }

  /**
   * Chuyển đổi đối tượng Product thành dữ liệu JSON để gửi lên server
   * @returns {Object} - Dữ liệu JSON
   */
  toJson() {
    return {
      id: this.id,
      code: this.code,
      name: this.name,
      categoryId: this.categoryId,
      uomId: this.uomId,
      vatType: this.vatType,
      colorExterior: this.colorExterior,
      colorInterior: this.colorInterior,
      productSegmentId1: this.productSegmentId1,
      productSegmentId2: this.productSegmentId2,
      productSegmentId3: this.productSegmentId3,
      productSegmentId4: this.productSegmentId4,
      description: this.description,
      parentId: this.parentId,
      importPrice: this.importPrice,
      sellingPrice: this.sellingPrice,
      brandId: this.brandId,
      companyId: this.companyId,
      version: this.version,
      type: this.type,
    };
  }

  /**
   * Lấy tên đầy đủ của sản phẩm (có thể bao gồm mã sản phẩm)
   * @returns {string} - Tên đầy đủ của sản phẩm
   */
  getFullName() {
    if (this.code) {
      return `${this.code} - ${this.name}`;
    }
    return this.name;
  }
}
